import Delete from "@mui/icons-material/Delete";
import { useFormikContext } from "formik";
import { useCallback } from "react";
import { useIntl } from "react-intl";

import type { ButtonProps } from "@citrine/atoms/button";
import { Button } from "@citrine/atoms/button";
import type { IconButtonProps } from "@citrine/atoms/icon-button";
import { IconButton } from "@citrine/atoms/icon-button";
import { Spinner } from "@citrine/atoms/loading";

import * as sx from "./buttons.sx";
import { useCloseContext } from "./dialog-button";
import { useFormConfiguration } from "./form";

type CustomButtonProps = Omit<ButtonProps, "type">;
/**
 * An icon button that is disabled in read-only mode, or while the form is
 * submitting/validating
 */
export function FormIconButton({ disabled, ...props }: IconButtonProps) {
  const { id: formId, readonly } = useFormConfiguration();
  const { isSubmitting, isValidating } = useFormikContext();

  return (
    <IconButton
      form={formId}
      {...props}
      disabled={disabled || isSubmitting || isValidating || readonly}
    />
  );
}

/**
 * BaseFormButton includes a ref to the form and is auto-disabled when submitting or validating
 */
function BaseFormButton({ disabled, ...props }: ButtonProps) {
  const { id: formId } = useFormConfiguration();
  const { isSubmitting, isValidating } = useFormikContext();

  return (
    <Button
      form={formId}
      {...props}
      disabled={disabled || isValidating || isSubmitting}
      startIcon={
        (isSubmitting || isValidating) &&
        props.showLoadingSpinner &&
        Boolean(props.startIcon) ? (
          <Spinner size={props.size === "small" ? 11 : 16} />
        ) : (
          props.startIcon
        )
      }
    />
  );
}

/**
 * FormButton extends BaseFormButton to be disabled in read-only mode
 */
export function FormButton({ disabled, ...props }: ButtonProps) {
  const { readonly } = useFormConfiguration();

  return <BaseFormButton {...props} disabled={disabled || readonly} />;
}

export function CancelButton(props: CustomButtonProps) {
  const { formatMessage } = useIntl();
  const { isSubmitting, isValidating } = useFormikContext();
  const { id: formId } = useFormConfiguration();
  const { id: dialogId, onClose } = useCloseContext();

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      props.onClick?.(event);
      // don't close nested forms
      if (formId === dialogId) {
        onClose?.();
      }
    },
    [dialogId, formId, onClose, props]
  );

  return (
    // BaseFormButton is not disabled in read-only mode; cancel should always work
    <BaseFormButton
      data-testid="cancel-button"
      children={formatMessage({ id: "cancel" })}
      variant="outlined"
      {...props}
      // does not disable in readonly mode
      disabled={props.disabled || isValidating || isSubmitting}
      onClick={onClick}
      type="button"
    />
  );
}

export function DeleteButton(props: Omit<CustomButtonProps, "color">) {
  const { formatMessage } = useIntl();

  return (
    <sx.DeleteButton
      as={FormButton}
      data-testid="delete-button"
      children={formatMessage({ id: "Delete" })}
      variant="outlined"
      startIcon={<Delete />}
      {...props}
    />
  );
}

export function ResetButton(props: CustomButtonProps) {
  const { formatMessage } = useIntl();
  const { resetForm } = useFormikContext();

  return (
    <FormButton
      data-testid="reset-button"
      children={formatMessage({ id: "form.button.reset" })}
      variant="outlined"
      {...props}
      type="reset"
      /**
       * type="reset" is only respected if no onClick is provided
       * in order to respect a custom onClick, we must manually reset the form
       */
      onClick={(...args) => {
        resetForm();
        props.onClick?.(...args);
      }}
    />
  );
}

export function SubmitButton(props: CustomButtonProps) {
  const { formatMessage } = useIntl();

  return (
    <FormButton
      data-testid="submit-button"
      children={formatMessage({ id: "form.button.submit" })}
      {...props}
      type="submit"
    />
  );
}
